import React, { useContext, useState } from "react";
import { TipKey, TipVariant, useTippy } from "@bloomberg/tippy";
import { useFortress } from "@foundation/fortress";
import { useScreen } from "@foundation/screen";

import { SURVEY_FEEDBACK_URL } from "~components/articles/basic/BasicContent";
import { Blens } from "~components/Blens";
import ContactUs from "~components/ContactUs";
import ErrorBoundary from "~components/ErrorBoundary";
import { GridLeftContent } from "~components/GridLayout";
import Share from "~components/Share";
import ShareFeedback from "~components/ShareFeedback";
import TipTout from "~components/TipTout";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import isEnabled from "~utils/features";
import { getFortressState } from "~utils/getFortressState";
import isOpinion from "~utils/isOpinion";

import styles from "./basicLeftRail.module.css";

type LeftRailSectionProps = {
    children: React.ReactNode;
};

const IS_BLENS_ENABLED = isEnabled("enableBlens");

export function TopLeftRail(props: LeftRailSectionProps): JSX.Element {
    return <div className={styles.top}>{props.children}</div>;
}

export function BottomLeftRail(props: LeftRailSectionProps): JSX.Element {
    return <div className={styles.bottom}>{props.children}</div>;
}

export default function BasicLeftRail(): JSX.Element {
    const { story } = useContext(CurrentStoryContext);
    const isOpinionBrand = isOpinion(story);
    const {
        breakpoints: { largeDesktop },
    } = useScreen();
    const fortressContext = useFortress();
    const { isAllowed } = getFortressState(fortressContext);

    const [giftArticleElement, setGiftArticleElement] =
        useState<Element | null>(null);

    useTippy({
        tipKey: TipKey.GIFT_ARTICLE,
        variant: TipVariant.POPOVER,
        refNode: giftArticleElement,
        additionalSelectorPath: "[data-component=outlined-button]",
    });

    return (
        <GridLeftContent>
            <TopLeftRail>
                <Share
                    className={styles.share}
                    showLabel={false}
                    giftArticleRef={setGiftArticleElement}
                />
                {IS_BLENS_ENABLED && largeDesktop && isAllowed && (
                    <ErrorBoundary fallback="failed to load broken ad">
                        <Blens
                            className={styles.blens}
                            quoteIds={story.blensSecurities}
                        />
                    </ErrorBoundary>
                )}
            </TopLeftRail>
            <BottomLeftRail>
                <ContactUs />
                {!isOpinionBrand && <TipTout />}
                <ShareFeedback
                    feedbackUrl={SURVEY_FEEDBACK_URL}
                    storySlug={story.slug}
                />
            </BottomLeftRail>
        </GridLeftContent>
    );
}
